import { MessageKey } from "../../i18n";
import { Surgery } from "../../types/patient.types";
import {
  FundamentoKey,
  GerarFundamentosRequest,
  HeaderRecurso,
  ItemRecurso,
  ItemTipo,
  NegativaFonte,
  Pedido,
  RecursoFinal,
  RecursosTextos,
  Referencia,
} from "../../types/resources.types";

export const STEP_KEYS = ["order", "items", "grounds", "confirm", "generated", "review", "done"] as const;
export type StepKey = (typeof STEP_KEYS)[number];

export const GROUND_KEYS: FundamentoKey[] = ["bibliografia", "cdc", "lgpd", "atoMedico", "resolucao"];

/** Fundamentos com texto próprio gerado pela IA (a bibliografia vira a lista de referências). */
export const TEXT_GROUNDS = ["cdc", "atoMedico", "lgpd", "resolucao"] as const;

export const EMPTY_TEXTS: RecursosTextos = { identificacaoNegativa: "", fundTec: "", cdc: "", atoMedico: "", lgpd: "", resolucao: "" };

/** Itens do pedido que podem entrar no recurso: "CODIGO descrição, …" e as tuplas [nome, qtd] de OPME. */
export function itemsFromSurgery(surgery: Surgery): ItemRecurso[] {
  const codes = String(surgery.procedimentos_validados ?? "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((part): ItemRecurso => {
      const [code] = part.split(" ");
      return { id: code, descricao: part.slice(code.length).trim(), tipo: "CODIGO", status: "NEGADO" };
    });

  const materials = (surgery.opme_list ?? [])
    .filter(([name]) => name)
    .map(([name, qty]): ItemRecurso => ({
      // Id derivado do nome: único dentro do pedido.
      id: `MAT-${String(name).toUpperCase().replace(/\s+/g, "-").slice(0, 30)}`,
      descricao: qty ? `${name} (qtd: ${qty})` : String(name),
      tipo: "MATERIAL",
      status: "NEGADO",
    }));

  return [...codes, ...materials];
}

type PayloadInput = {
  grounds?: FundamentoKey[];
  denialSource: NegativaFonte;
  denialText: string;
  items: ItemRecurso[];
  doctorName?: string;
  email?: string;
  /** Só esse tipo de item vai no payload (recarregar uma seção das referências). */
  kind?: ItemTipo;
};

export function buildAppealPayload({ grounds, denialSource, denialText, items, doctorName, email, kind }: PayloadInput): GerarFundamentosRequest {
  const ofKind = (tipo: ItemTipo) => (!kind || kind === tipo ? items.filter((i) => i.tipo === tipo) : undefined);
  return {
    fundamentos: grounds,
    negativa: denialSource === "texto" ? denialText : undefined,
    procedimentos: ofKind("CODIGO"),
    opmes: ofKind("MATERIAL"),
    medico_nome: doctorName || undefined,
    email,
  };
}

const SUMMARY_LABELS: Record<FundamentoKey, string> = {
  bibliografia: "fundamentação técnica",
  cdc: "CDC",
  atoMedico: "Ato Médico",
  lgpd: "LGPD",
  resolucao: "Resolução 2.448/2025 (CFM)",
};

/** Resumo salvo no cabeçalho do recurso. */
export const appealSummary = (grounds: FundamentoKey[]) =>
  grounds.length ? `Recurso com ${grounds.map((g) => SUMMARY_LABELS[g]).join(" + ")}.` : "Recurso sem fundamentação selecionada.";

type BlockInput = {
  order: Pedido | null;
  items: number;
  grounds: number;
  hasRefs: boolean;
  selectedRefs: number;
  linksOpened: boolean;
  declared: boolean;
};

/** Por que o passo atual não pode avançar (null = pode). */
export function blockReason(step: StepKey, s: BlockInput): MessageKey | null {
  if (step === "order" && !s.order) return "appeals.blocked.order";
  if (step === "items" && s.items === 0) return "appeals.blocked.items";
  if (step === "grounds" && s.grounds === 0) return "appeals.blocked.grounds";
  if (step === "review") {
    if (s.hasRefs && s.selectedRefs === 0) return "appeals.blocked.refs";
    if (s.hasRefs && !s.linksOpened) return "appeals.blocked.links";
    if (!s.declared) return "appeals.blocked.declaration";
  }
  return null;
}

/** Documento final enviado para o PDF e salvo no pedido. */
export function buildAppeal(
  header: HeaderRecurso,
  order: Pedido,
  items: ItemRecurso[],
  grounds: FundamentoKey[],
  texts: RecursosTextos,
  refs: Referencia[]
): RecursoFinal {
  return {
    payload: {
      header,
      pedido: { id: order.id, codigo: order.codigo, paciente: order.paciente, procedimento: order.procedimento, data: order.data },
      itens: items.map(({ id, descricao, tipo, status }) => ({ id, descricao, tipo, status })),
      fundamentosSelecionados: grounds,
      textos: {
        identificacaoNegativa: texts.identificacaoNegativa,
        fundamentacaoTecnica: texts.fundTec,
        fundamentacaoCdc: texts.cdc,
        fundamentacaoAtoMedico: texts.atoMedico,
        fundamentacaoLgpd: texts.lgpd,
        fundamentacaoResolucao: texts.resolucao,
      },
    },
    referencias_selecionadas: refs.map((r) => ({
      id: r.id,
      procedimento_alvo: r.procedimento_alvo || "",
      opme_alvo: r.opme_alvo || "",
      tipo: r.tipo,
      alvo_tipo: r.procedimento_alvo ? "procedimento" : r.opme_alvo ? "opme" : null,
      autores: r.autores,
      ano: r.ano,
      titulo: r.titulo,
      fonte: r.fonte,
      url: r.url,
      doi: r.doi,
      base: r.base,
      citacao_abnt: r.citacao_abnt,
    })),
  };
}
